import type {
  ModelSelection,
  ProviderInstanceId,
  ProviderOptionSelection,
  ServerProvider,
  ServerProviderModel,
} from "@afar-research/contracts";
import type { UnifiedSettings } from "@afar-research/contracts/settings";
import {
  buildProviderOptionSelectionsFromDescriptors,
  createModelSelection,
  getProviderOptionDescriptors,
} from "@afar-research/shared/model";
import {
  isProviderReadyForProjectCreation,
  isProviderStarting,
} from "@afar-research/shared/providerCapabilities";

import { resolveAppModelSelectionForInstance } from "./modelSelection";
import { getProviderModelCapabilities } from "./providerModels";
import { deriveProviderInstanceEntries, type ProviderInstanceEntry } from "./providerInstances";

export interface ProjectCreationProviderOption {
  readonly instanceId: ProviderInstanceId;
  readonly label: string;
  readonly modelSelection: ModelSelection;
}

/**
 * Whether the Environment can start a new project's ingest thread now, may be able
 * to once a starting instance settles, or cannot without the user's help.
 */
export type ProjectCreationProviderReadiness = "ready" | "starting" | "unavailable";

export interface ProjectCreationProviderSelection {
  readonly readiness: ProjectCreationProviderReadiness;
  /** Instances that can currently start a new project's ingest thread. */
  readonly options: ReadonlyArray<ProjectCreationProviderOption>;
  /**
   * Every configured instance, eligible or not: the recovery panel names the
   * one holding creation up. The fallback picker offers only the eligible ones
   * (`projectCreationFallbackEntries`).
   */
  readonly instanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  readonly modelOptionsByInstance: ReadonlyMap<
    ProviderInstanceId,
    ReadonlyArray<ServerProviderModel>
  >;
  readonly preselected: ModelSelection | null;
}

/**
 * Resolve only exact Provider Instances that can currently create new project's
 * ingest threads. The Environment default is eligible for preselection;
 * another ready instance is never chosen implicitly.
 *
 * A Cloud Workspace (`managedByAfar`) also offers its Codex while it is still
 * starting: the subscription includes it, so there is nothing for the user to
 * set up or wait for, and the Environment holds the submit until it is ready.
 */
export function resolveProjectCreationProviderSelection(input: {
  readonly settings: UnifiedSettings;
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly managedByAfar?: boolean;
}): ProjectCreationProviderSelection {
  const environmentDefault = input.settings.defaultAgentModelSelection;
  const instanceEntries = deriveProviderInstanceEntries(input.providers);
  const options = instanceEntries.flatMap((entry): ReadonlyArray<ProjectCreationProviderOption> => {
    const startingManagedInstance =
      input.managedByAfar === true &&
      isProviderStarting(entry.snapshot) &&
      entry.snapshot.instanceCapabilities?.threadCreation.projectDefinition === true;
    if (
      !startingManagedInstance &&
      !isProviderReadyForProjectCreation(input.providers, entry.instanceId)
    ) {
      return [];
    }
    const isEnvironmentDefault = entry.instanceId === environmentDefault.instanceId;
    const model = resolveAppModelSelectionForInstance(
      entry.instanceId,
      input.settings,
      input.providers,
      isEnvironmentDefault ? environmentDefault.model : null,
    );
    if (model === null) {
      return [];
    }
    return [
      {
        instanceId: entry.instanceId,
        label: entry.displayName,
        modelSelection: createModelSelection(
          entry.instanceId,
          model,
          isEnvironmentDefault ? environmentDefault.options : undefined,
        ),
      },
    ];
  });
  const defaultOption = options.find(
    (option) => option.instanceId === environmentDefault.instanceId,
  );
  return {
    readiness:
      options.length > 0
        ? "ready"
        : input.providers.some(isProviderStarting)
          ? "starting"
          : "unavailable",
    options,
    instanceEntries,
    modelOptionsByInstance: new Map(
      instanceEntries.map((entry) => [entry.instanceId, entry.models]),
    ),
    preselected: defaultOption?.modelSelection ?? null,
  };
}

/**
 * The selection while the Environment's provider snapshot has not reached this
 * client, for example right after a Cloud Workspace starts.
 */
export const PENDING_PROJECT_CREATION_PROVIDER_SELECTION: ProjectCreationProviderSelection = {
  readiness: "starting",
  options: [],
  instanceEntries: [],
  modelOptionsByInstance: new Map(),
  preselected: null,
};

/**
 * What the dialog says in place of the form while no instance can start a
 * new project's ingest thread. A starting instance is shown as progress, never as
 * a missing requirement; the form replaces it once the instance is ready.
 */
export function describeProjectCreationProviderRecovery(
  selection: Pick<ProjectCreationProviderSelection, "readiness" | "instanceEntries">,
  environment: { readonly managedByAfar: boolean },
): {
  readonly headline: string;
  readonly detail: string;
  /** Whether Provider Settings can resolve it; never while starting or in a Cloud Workspace. */
  readonly offersProviderSettings: boolean;
} {
  if (selection.readiness === "starting") {
    const starting = selection.instanceEntries.find((entry) => isProviderStarting(entry.snapshot));
    return {
      headline: `Starting ${starting?.displayName ?? "the Agent Provider"}…`,
      detail: "This form opens as soon as it is ready.",
      offersProviderSettings: false,
    };
  }
  if (environment.managedByAfar) {
    return {
      headline: "Agent Provider unavailable",
      detail:
        "AFAR manages the Agent Provider in this Cloud Workspace, so there is nothing to configure, but it did not become ready. Send feedback from the sidebar so we can look into it.",
      offersProviderSettings: false,
    };
  }
  return {
    headline: "Agent Provider required",
    detail:
      "Complete the existing provider onboarding prerequisite, then return to create this project.",
    offersProviderSettings: true,
  };
}

/**
 * Why an instance offered by the picker cannot start a project creation
 * Thread, or `null` when it can. The picker shows the reason on the row.
 *
 * Only instances the picker is already willing to list reach this: it filters
 * its own rows to enabled, available, ready instances. So the reasons here
 * cover the narrower gap between "ready enough to pick" and "ready to start a
 * new project's ingest thread" — provider setup that has not finished, an empty
 * model catalog, or a missing capability.
 */
export function describeProjectCreationInstanceIneligibility(
  selection: ProjectCreationProviderSelection,
  instanceId: ProviderInstanceId,
): string | null {
  if (selection.options.some((option) => option.instanceId === instanceId)) {
    return null;
  }
  const entry = selection.instanceEntries.find((candidate) => candidate.instanceId === instanceId);
  if (entry === undefined) {
    return "This Provider Instance is no longer configured.";
  }
  return `${entry.displayName} ${instanceIneligibility(entry)}.`;
}

/** What keeps a listed instance from starting a new project's ingest thread. */
function instanceIneligibility(entry: ProviderInstanceEntry): string {
  if (entry.snapshot.readiness !== "ready") return "has not finished provider setup";
  if (entry.models.length === 0) return "has no models available";
  return "cannot start a new project's ingest thread";
}

/**
 * Why the dialog asks for a Provider Instance at all, or `null` when it does
 * not: a new project uses the default in Settings, and only when that default
 * cannot start the project does the user pick another ready instance. Picking
 * one leaves the Settings default as it was.
 */
export function describeProjectCreationProviderFallback(
  selection: ProjectCreationProviderSelection,
  settings: Pick<UnifiedSettings, "defaultAgentModelSelection">,
): string | null {
  if (selection.preselected !== null) return null;
  const defaultInstanceId = settings.defaultAgentModelSelection.instanceId;
  const entry = selection.instanceEntries.find(
    (candidate) => candidate.instanceId === defaultInstanceId,
  );
  if (entry === undefined) {
    return "The default Provider Instance in Settings is no longer configured. Choose a ready one for this project.";
  }
  return `${entry.displayName}, the default in Settings, ${instanceIneligibility(entry)}. Choose another ready Provider Instance for this project.`;
}

/** The instances the fallback picker offers: only those that can start the project now. */
export function projectCreationFallbackEntries(
  selection: ProjectCreationProviderSelection,
): ReadonlyArray<ProviderInstanceEntry> {
  const eligible = new Set(selection.options.map((option) => option.instanceId));
  return selection.instanceEntries.filter((entry) => eligible.has(entry.instanceId));
}

export interface ProjectCreationProviderChoice {
  readonly instanceId: ProviderInstanceId;
  readonly model: string;
  /** Provider traits for this instance and model, such as reasoning effort. */
  readonly options?: ReadonlyArray<ProviderOptionSelection> | undefined;
}

/**
 * The provider traits a model starts with, so a new project's ingest thread
 * carries them whichever instance was chosen. Only the Environment default
 * has traits saved in settings; every other instance starts from its model's
 * own descriptor defaults rather than from nothing.
 */
export function defaultProjectCreationProviderOptions(
  selection: ProjectCreationProviderSelection,
  chosen: { readonly instanceId: ProviderInstanceId; readonly model: string },
): ReadonlyArray<ProviderOptionSelection> | undefined {
  const entry = selection.instanceEntries.find(
    (candidate) => candidate.instanceId === chosen.instanceId,
  );
  if (entry === undefined) {
    return undefined;
  }
  const descriptors = getProviderOptionDescriptors({
    caps: getProviderModelCapabilities(entry.models, chosen.model, entry.driverKind),
    selections: undefined,
  });
  const built = buildProviderOptionSelectionsFromDescriptors(descriptors);
  return built === undefined || built.length === 0 ? undefined : built;
}

/**
 * Build the `ModelSelection` for an explicitly chosen instance, model and set
 * of provider traits. Returns `null` when the instance cannot start a new project's
 * ingest thread, so an ineligible choice can never be submitted.
 */
export function resolveProjectCreationModelSelection(
  selection: ProjectCreationProviderSelection,
  chosen: ProjectCreationProviderChoice | null,
): ModelSelection | null {
  if (chosen === null) {
    return null;
  }
  const option = selection.options.find((candidate) => candidate.instanceId === chosen.instanceId);
  if (option === undefined) {
    return null;
  }
  const model = chosen.model === "" ? option.modelSelection.model : chosen.model;
  // Traits the user set win; otherwise fall back to whatever the option
  // resolved, which is the Environment default's saved traits for that one
  // instance and nothing for the rest.
  const options = chosen.options ?? option.modelSelection.options;
  return createModelSelection(chosen.instanceId, model, options);
}
